import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { can, type Role } from "@family/shared";
import type {
  DatePrecision,
  MediaRef,
  UpdateVoiceNoteTranscriptRequest,
  VoiceNote,
  VoiceNotesListResponse,
  VoiceNoteTranslationResponse,
} from "@family/shared";

interface MediaStoryProps {
  familySlug: string;
  mediaId: string;
  caption?: string | null;
  takenAt?: string | null;
  takenAtPrecision?: DatePrecision | null;
  voiceNoteCount?: number;
  /** Lets the recorder correct their own transcript. */
  currentUserId?: string | undefined;
  /** Moderators and admins can correct anyone's. */
  canEditAnyTranscript?: boolean;
  /** Members who can record can also ask for a missing transcript. */
  canTranscribe?: boolean;
}

function formatTaken(iso: string, precision: DatePrecision | null | undefined): string {
  const d = new Date(iso);
  if (precision === "year") return `c. ${d.getUTCFullYear()}`;
  if (precision === "month") {
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", timeZone: "UTC" });
  }
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** apiFetch errors read "API 429: {"error":"…"}"; the server's sentence is
 *  the useful part when it wrote one for people (the AI allowance message). */
function friendlyError(err: Error, fallback: string): string {
  const m = /^API 429: (.*)$/s.exec(err.message);
  if (!m) return fallback;
  try {
    return (JSON.parse(m[1]!) as { error?: string }).error ?? fallback;
  } catch {
    return fallback;
  }
}

const LANGUAGE_LABELS: Record<string, string> = { am: "Amharic", ti: "Tigrinya", en: "English" };

/** Caption, year and spoken stories under a photo. Recordings are only
 *  fetched (and their URLs signed) when someone asks to listen or read. */
export function MediaStory({
  familySlug,
  mediaId,
  caption,
  takenAt,
  takenAtPrecision,
  voiceNoteCount = 0,
  currentUserId,
  canEditAnyTranscript = false,
  canTranscribe = false,
}: MediaStoryProps) {
  const [open, setOpen] = useState<"listen" | "read" | null>(null);
  const notesQuery = useQuery<VoiceNotesListResponse>({
    queryKey: ["voice-notes", familySlug, mediaId],
    queryFn: () =>
      apiFetch<VoiceNotesListResponse>(`/families/${familySlug}/media/${mediaId}/voice-notes`),
    enabled: open !== null,
    // A transcript being written appears on its own a few seconds later.
    refetchInterval: (q) =>
      q.state.data?.voiceNotes.some((n) => n.transcriptStatus === "pending") ? 4000 : false,
  });

  if (!caption && !takenAt && voiceNoteCount === 0) return null;

  return (
    <div className="stack" style={{ gap: 4, marginTop: 6, fontSize: "0.9rem" }}>
      {takenAt && <div className="text-subtle">{formatTaken(takenAt, takenAtPrecision)}</div>}
      {caption && <div style={{ whiteSpace: "pre-wrap" }}>{caption}</div>}
      {voiceNoteCount > 0 && open === null && (
        <div style={{ display: "flex", gap: 12 }}>
          <button type="button" className="btn-tertiary" style={{ paddingLeft: 0 }} onClick={() => setOpen("listen")}>
            ▶ Listen to the story{voiceNoteCount > 1 ? ` (${voiceNoteCount})` : ""}
          </button>
          <button type="button" className="btn-tertiary" onClick={() => setOpen("read")}>
            Read
          </button>
        </div>
      )}
      {open && notesQuery.isLoading && <div className="text-muted">Loading…</div>}
      {open && notesQuery.error && <div className="text-error">Couldn't load the recording.</div>}
      {notesQuery.data?.voiceNotes.map((n, i) => (
        <div key={n.id} className="stack" style={{ gap: 2 }}>
          {n.recordedBy.displayName && (
            <div className="text-subtle" style={{ fontSize: "0.8rem" }}>Told by {n.recordedBy.displayName}</div>
          )}
          <audio src={n.url} controls preload="metadata" autoPlay={open === "listen" && i === 0} style={{ width: "100%" }} />
          <VoiceNoteTranscript
            note={n}
            familySlug={familySlug}
            mediaId={mediaId}
            canEdit={canEditAnyTranscript || n.recordedBy.id === currentUserId}
            canTranscribe={canTranscribe}
          />
        </div>
      ))}
    </div>
  );
}

/** The spoken stories under a post's photos. The caption and year already
 *  show as the post's text and date, so only the recordings appear here. */
export function PostMediaStories({
  familySlug,
  media,
  role,
  currentUserId,
}: {
  familySlug: string;
  media: MediaRef[];
  role: Role;
  currentUserId?: string | undefined;
}) {
  const told = media
    .map((m, i) => ({ m, position: i + 1 }))
    .filter(({ m }) => (m.voiceNoteCount ?? 0) > 0);
  if (told.length === 0) return null;
  return (
    <div className="stack" style={{ gap: "var(--s-2)", marginBottom: "var(--s-3)" }}>
      {told.map(({ m, position }) => (
        <div key={m.id}>
          {media.length > 1 && (
            <div className="text-subtle" style={{ fontSize: "0.8rem" }}>Photo {position}</div>
          )}
          <MediaStory
            familySlug={familySlug}
            mediaId={m.id}
            voiceNoteCount={m.voiceNoteCount}
            currentUserId={currentUserId}
            canEditAnyTranscript={can(role, "post:delete:any")}
            canTranscribe={can(role, "media:upload")}
          />
        </div>
      ))}
    </div>
  );
}

function VoiceNoteTranscript({
  note,
  familySlug,
  mediaId,
  canEdit,
  canTranscribe,
}: {
  note: VoiceNote;
  familySlug: string;
  mediaId: string;
  canEdit: boolean;
  canTranscribe: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [showEnglish, setShowEnglish] = useState(false);
  const base = `/families/${familySlug}/media/${mediaId}/voice-notes/${note.id}`;
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["voice-notes", familySlug, mediaId] });

  const transcribe = useMutation({
    mutationFn: () => apiFetch(`${base}/transcribe`, { method: "POST" }),
    onSettled: refresh,
  });
  const save = useMutation({
    mutationFn: (text: string) => {
      const body: UpdateVoiceNoteTranscriptRequest = { transcript: text };
      return apiFetch(`${base}/transcript`, { method: "PATCH", body: JSON.stringify(body) });
    },
    onSuccess: async () => {
      await refresh();
      setDraft(null);
    },
  });
  const translate = useMutation({
    mutationFn: () => apiFetch<VoiceNoteTranslationResponse>(`${base}/translate`, { method: "POST" }),
    onSuccess: async () => {
      await refresh();
      setShowEnglish(true);
    },
  });

  if (draft !== null) {
    return (
      <div className="stack" style={{ gap: 4 }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={Math.min(10, Math.max(3, Math.ceil(draft.length / 60)))}
          lang={note.transcriptLang ?? undefined}
          aria-label="Transcript"
          style={{ width: "100%" }}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" disabled={save.isPending} onClick={() => save.mutate(draft)}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button type="button" className="btn-tertiary" onClick={() => setDraft(null)}>
            Cancel
          </button>
        </div>
        {save.error && <div className="text-error">Couldn't save — please try again.</div>}
      </div>
    );
  }

  const busy = note.transcriptStatus === "pending" || transcribe.isPending;
  const langLabel = note.transcriptLang ? LANGUAGE_LABELS[note.transcriptLang] : undefined;
  const canTranslate = Boolean(note.transcript) && note.transcriptLang !== "en";

  return (
    <div className="stack" style={{ gap: 4 }}>
      {note.transcript && (
        <blockquote lang={note.transcriptLang ?? undefined} style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {note.transcript}
        </blockquote>
      )}
      {busy && <div className="text-muted">Writing down what was said…</div>}
      {!busy && note.transcriptStatus === "empty" && (
        <div className="text-muted">No words could be made out in this recording.</div>
      )}
      {!busy && note.transcriptStatus === "failed" && (
        <div className="text-muted">This recording couldn't be written down automatically.</div>
      )}
      {showEnglish && note.transcriptEn && (
        <div className="stack" style={{ gap: 2 }}>
          <div className="text-subtle" style={{ fontSize: "0.8rem" }}>In English (machine translation)</div>
          <div lang="en" style={{ whiteSpace: "pre-wrap" }}>{note.transcriptEn}</div>
        </div>
      )}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", fontSize: "0.85rem" }}>
        {langLabel && note.transcript && <span className="text-subtle">{langLabel}</span>}
        {canTranslate && !(showEnglish && note.transcriptEn) && (
          <button
            type="button"
            className="btn-tertiary"
            disabled={translate.isPending}
            onClick={() => (note.transcriptEn ? setShowEnglish(true) : translate.mutate())}
          >
            {translate.isPending ? "Translating…" : "Translate to English"}
          </button>
        )}
        {showEnglish && note.transcriptEn && (
          <button type="button" className="btn-tertiary" onClick={() => setShowEnglish(false)}>
            Hide English
          </button>
        )}
        {!busy && canTranscribe && (note.transcriptStatus === null || note.transcriptStatus === "failed") && (
          <button type="button" className="btn-tertiary" onClick={() => transcribe.mutate()}>
            {note.transcriptStatus === "failed" ? "Try writing it down again" : "Write down what was said"}
          </button>
        )}
        {!busy && canEdit && (
          <button type="button" className="btn-tertiary" onClick={() => setDraft(note.transcript ?? "")}>
            {note.transcript ? "Correct the text" : "Type it yourself"}
          </button>
        )}
      </div>
      {translate.error && <div className="text-error">{friendlyError(translate.error, "Couldn't translate right now — please try again later.")}</div>}
      {transcribe.error && <div className="text-error">{friendlyError(transcribe.error, "Couldn't write it down right now — please try again later.")}</div>}
    </div>
  );
}
